/**
 * Cart page quantity steppers — progressive enhancement over the SSR cart form.
 * Adjusts the keyed updates[<line key>] inputs and submits the form (full page
 * reload). No Cart Ajax.
 *
 * The quick cart drawer is not rendered on this template, so the page is the
 * only live view of the cart. Add to cart from an upsell here re-renders the
 * section rather than opening a drawer.
 */
(() => {
  const ROOT = window.Shopify?.routes?.root || '/';
  // cart_refresh: an add Shopify rejected may still have added part of the quantity
  const ATC_EVENTS = ['product_atc_via_form', 'product_atc_via_quick_add', 'cart_refresh'];

  // The line's max (basket limit, stock or catalog rule) is rendered on the
  // input. Clamp to it so a click or typed value cannot submit above it.
  const clampToMax = (input, value) => {
    const max = Number.parseInt(input.max, 10);

    return Number.isNaN(max) ? value : Math.min(value, max);
  };

  const swapChild = (liveRoot, incomingRoot, selector) => {
    const current = liveRoot.querySelector(selector);
    const nextEl = incomingRoot.querySelector(selector);

    if (current && nextEl) {
      current.replaceWith(nextEl);
      return;
    }

    if (current && !nextEl) current.remove();
  };

  // See quick-cart.js: the live wallet node must never be moved, so only the
  // parts around it are replaced in place.
  const SUMMARY_PARTS = [
    '.cart-summary__heading',
    '.cart-summary__lines',
    '.cart-summary__tax-note',
    '.cart-summary__checkout',
    '.cart-summary__note',
  ];

  const patchSummaryAroundWallets = (liveSummary, nextSummary) => {
    const samePartsPresent = SUMMARY_PARTS.every(
      (selector) =>
        Boolean(liveSummary.querySelector(selector)) ===
        Boolean(nextSummary.querySelector(selector))
    );

    if (!samePartsPresent) return false;

    liveSummary.className = nextSummary.className;

    for (const selector of SUMMARY_PARTS) {
      const current = liveSummary.querySelector(selector);
      const incoming = nextSummary.querySelector(selector);

      if (current && incoming) current.replaceWith(incoming);
    }

    return true;
  };

  class CartPage extends HTMLElement {
    #refreshToken = 0;
    #refreshing = false;
    #giftPending = new Set();

    connectedCallback() {
      this.addEventListener('click', this.#onClick);
      this.addEventListener('change', this.#onChange);
      this.addEventListener('keydown', this.#onKeydown);
      this.addEventListener('submit', this.#onSubmit);
      this.addEventListener('free-gift:pending', this.#onFreeGiftPending);
      this.addEventListener('free-gift:settled', this.#onFreeGiftSettled);
      this.addEventListener('free-gift:updated', this.#onCartUpdated);

      for (const eventName of ATC_EVENTS) {
        document.addEventListener(eventName, this.#onCartUpdated);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#onClick);
      this.removeEventListener('change', this.#onChange);
      this.removeEventListener('keydown', this.#onKeydown);
      this.removeEventListener('submit', this.#onSubmit);
      this.removeEventListener('free-gift:pending', this.#onFreeGiftPending);
      this.removeEventListener('free-gift:settled', this.#onFreeGiftSettled);
      this.removeEventListener('free-gift:updated', this.#onCartUpdated);

      for (const eventName of ATC_EVENTS) {
        document.removeEventListener(eventName, this.#onCartUpdated);
      }
    }

    #form() {
      return this.querySelector('form[data-cart-form]');
    }

    #onClick = (event) => {
      const remove = event.target.closest('[data-cart-remove]');

      // Remove is a link, so it navigates on its own. An in-flight gift mutation
      // would land after the shopper has left, against a cart it no longer fits.
      if (this.#isLocked() && remove) {
        event.preventDefault();
        return;
      }

      if (remove) {
        const line = remove.closest('[data-cart-line-item]');

        if (line?.hasAttribute('data-promotional-gift')) {
          window.themeFreeGift?.suppressPromotion?.(
            line.getAttribute('data-gwp-promotion') || ''
          );
        }
      }

      const minus = event.target.closest('[data-cart-quantity-minus]');
      const plus = event.target.closest('[data-cart-quantity-plus]');

      if (!minus && !plus) return;

      event.preventDefault();

      const line = event.target.closest('[data-cart-line-item]');
      if (!line) return;

      const input = line.querySelector('[data-cart-quantity-input]');
      if (!(input instanceof HTMLInputElement)) return;

      const current = Number.parseInt(input.value, 10) || 1;
      const next = minus ? Math.max(1, current - 1) : clampToMax(input, current + 1);

      if (next === current) return;

      input.value = String(next);
      this.#submitForm();
    };

    #onChange = (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (!input.hasAttribute('data-cart-quantity-input')) return;

      this.#normalizeQuantityInput(input);
      this.#submitForm();
    };

    #onKeydown = (event) => {
      if (event.key !== 'Enter') return;

      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;
      if (!input.hasAttribute('data-cart-quantity-input')) return;

      // Avoid implicit submit via Checkout; update the cart instead.
      event.preventDefault();
      this.#normalizeQuantityInput(input);
      this.#submitForm();
    };

    #onCartUpdated = () => {
      // Line keys and the totals are stale until the section re-renders, so no
      // submit — checkout included — may leave until then.
      this.#refreshing = true;
      this.#syncBusy();
      this.#refresh();
    };

    #onFreeGiftPending = (event) => {
      this.#giftPending.add(event.detail?.id);
      this.#syncBusy();
    };

    #onFreeGiftSettled = (event) => {
      this.#giftPending.delete(event.detail?.id);
      this.#syncBusy();
    };

    #onSubmit = (event) => {
      if (!this.#isLocked()) return;

      event.preventDefault();
    };

    #isLocked() {
      return this.#refreshing || this.#giftPending.size > 0;
    }

    #syncBusy() {
      const locked = this.#isLocked();

      // Dims the form and takes it out of the pointer flow, which covers the
      // quantity controls and the remove links in one place.
      this.toggleAttribute('data-busy', locked);

      // Wallets post straight to checkout without the cart form, so the
      // form-level guards never see them.
      const checkout = this.querySelector('[name="checkout"]');
      if (checkout instanceof HTMLButtonElement) {
        checkout.disabled = locked;
      }

      const wallets = this.querySelector('[data-cart-wallets]');
      if (wallets instanceof HTMLElement) {
        wallets.inert = locked;

        // Must be the literal "true" for the stylesheet's
        // [aria-disabled='true'] pointer-events guard to apply.
        if (locked) {
          wallets.setAttribute('aria-disabled', 'true');
        } else {
          wallets.removeAttribute('aria-disabled');
        }
      }
    }

    #normalizeQuantityInput(input) {
      // Keep a minimum of 1 — line removal is via the remove control, not qty 0.
      const value = Number.parseInt(input.value, 10);
      if (Number.isNaN(value) || value < 1) {
        input.value = '1';
        return;
      }

      input.value = String(clampToMax(input, value));
    }

    #submitForm() {
      if (this.#isLocked()) return;

      const form = this.#form();
      if (!form) return;

      // Use form.submit() so the checkout submitter name is not included.
      form.submit();
    }

    async #refresh() {
      const sectionId = this.getAttribute('data-section-id');

      if (!sectionId) {
        window.location.reload();
        return;
      }

      const url = `${ROOT}cart?sections=${encodeURIComponent(sectionId)}`;
      // Later refreshes win, so an out-of-order response never restores old markup.
      const token = ++this.#refreshToken;

      try {
        // No JSON Accept header: /cart answers that with the cart payload, not sections.
        const response = await fetch(url);

        if (token !== this.#refreshToken) return;
        if (!response.ok) throw new Error('Cart section request failed');

        const data = await response.json();
        if (token !== this.#refreshToken) return;

        const template = document.createElement('template');
        template.innerHTML = (data[sectionId] || '').trim();
        const next = template.content.querySelector('cart-page');
        if (!next) throw new Error('Cart section markup missing');

        window.themeCarousel?.destroyAll?.(this);
        const initWallets = this.#adoptMarkup(next);

        this.#hydrateInjectedContent({ initWallets });
        this.#refreshing = false;
        // The replaced markup carries a fresh, enabled checkout button.
        this.#syncBusy();
        this.#syncHeaderCount();
      } catch (error) {
        if (token !== this.#refreshToken) return;

        // The form still holds stale line keys, so a reload is the only safe recovery.
        window.location.reload();
      }
    }

    #adoptMarkup(next) {
      const liveWallets = this.querySelector('[data-cart-wallets]');
      const nextSummary = next.querySelector('.cart-summary');
      const nextHasWallets = Boolean(nextSummary?.querySelector('[data-cart-wallets]'));

      this.className = next.className;
      if (next.hasAttribute('style')) {
        this.setAttribute('style', next.getAttribute('style') || '');
      }

      swapChild(this, next, 'free-gift-controller');
      swapChild(this, next, '.cart-page__header');
      swapChild(this, next, '.cart-page__promo');
      swapChild(this, next, '.cart-page__items');
      swapChild(this, next, '.cart-page__blocks');

      const liveForm = this.querySelector('[data-cart-form]');
      const nextForm = next.querySelector('[data-cart-form]');
      const liveSummary = this.querySelector('.cart-summary');
      const sidebar = this.querySelector('.cart-page__sidebar');

      if (!nextForm) {
        liveForm?.remove();
        return false;
      }

      if (!liveForm) {
        this.querySelector('.cart-page__inner')?.insertBefore(
          nextForm,
          this.querySelector('.cart-page__blocks')
        );
        return nextHasWallets;
      }

      if (
        liveSummary &&
        nextSummary &&
        liveWallets instanceof HTMLElement &&
        nextHasWallets &&
        patchSummaryAroundWallets(liveSummary, nextSummary)
      ) {
        return false;
      }

      if (nextSummary && sidebar) {
        if (liveSummary) {
          liveSummary.replaceWith(nextSummary);
        } else {
          sidebar.append(nextSummary);
        }

        return nextHasWallets;
      }

      liveSummary?.remove();
      return false;
    }

    #hydrateInjectedContent({ initWallets = false } = {}) {
      requestAnimationFrame(() => {
        if (initWallets) {
          window.Shopify?.PaymentButton?.init?.();
        }

        window.themeCarousel?.evaluateAll?.(this);
      });
    }

    async #syncHeaderCount() {
      // The header is a separate section and is not re-rendered with this one.
      const response = await fetch(`${ROOT}cart.js`, {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) return;

      const cart = await response.json();
      const count = Number(cart.item_count) || 0;

      for (const badge of document.querySelectorAll('[data-cart-count]')) {
        badge.hidden = count === 0;
        badge.textContent = count > 0 ? String(count) : '';
      }
    }
  }

  if (!customElements.get('cart-page')) {
    customElements.define('cart-page', CartPage);
  }
})();
